// Install-level events the game tables never see.
//
// Everything the database knows about a player starts at start-solo, so an
// app that is opened and closed on Home, or a start that dies on the network,
// leaves no trace: in the week of 6-12 October about half of Google's counted
// installs never answered a question and nothing could say where they went.
// These rows are that first step, plus the Daily reminder's opt-in and taps.
//
// Written through the log_app_event RPC (supabase/migrations/
// 20261011_app_events.sql), fire and forget: a log that fails must never
// cost the player anything, and the RPC itself ignores what it does not know.

import { App } from '@capacitor/app';
import { supabase } from '../supabase';
import { isNative } from '../utils/platform';
import { IS_EMBED } from './embed';
import { getDeviceId } from './deviceId';

export type AppEventName =
  | 'app_open'
  | 'match_start_failed'
  | 'reminder_prompt'
  | 'reminder_on'
  | 'reminder_off'
  | 'reminder_denied'
  | 'reminder_open'
  // 20261012_badges.sql: the share cards, the badge shelf, and the seat taken
  // from an invite (TRAVEL_COMPANION counts those)
  | 'share_tap'
  | 'share_result'
  | 'badge_shelf_open'
  | 'invite_join';

type Props = Record<string, string | number | boolean | null>;

// Read once, at load: the router rewrites the address soon after. The share
// cards tag their links (utm_source=share, utm_medium=score, daily, atlas or
// badge), so the first open of a visit that came from one says so.
const LAUNCH_SEARCH = typeof window !== 'undefined' ? window.location.search : '';

function launchSource(): Props {
  if (isNative) return {};
  try {
    const p = new URLSearchParams(LAUNCH_SEARCH);
    const out: Props = {};
    for (const k of ['utm_source', 'utm_medium', 'utm_content']) {
      const v = p.get(k);
      if (v) out[k] = v.slice(0, 40);
    }
    return out;
  } catch {
    return {};
  }
}

let build: Promise<number | null> | null = null;

/** The Android versionCode, read once; null on the web. */
function appBuild(): Promise<number | null> {
  if (!isNative) return Promise.resolve(null);
  if (!build) build = App.getInfo().then((i) => parseInt(i.build, 10) || null).catch(() => null);
  return build;
}

export function logAppEvent(event: AppEventName, props?: Props): void {
  // Dev servers and the browser pane talk to the production database; their
  // opens would only blur the numbers this exists to read.
  if (import.meta.env.DEV) return;
  void (async () => {
    try {
      await supabase.rpc('log_app_event', {
        p_device_id: getDeviceId(),
        p_event: event,
        p_platform: isNative ? 'android' : IS_EMBED ? 'embed' : 'web',
        p_build: await appBuild(),
        p_props: props ?? null,
      });
    } catch { /* analytics never blocks play */ }
  })();
}

/** How long the app must sit in the background before coming back counts as
 *  another open. Shorter returns are the same visit (a notification shade, a
 *  quick switch to another app). */
const REOPEN_AFTER_MS = 30 * 60 * 1000;

/**
 * Logs the cold start once, then every return from the background after
 * REOPEN_AFTER_MS. Returns a cleanup for the native listeners.
 */
export function trackAppOpens(): () => void {
  logAppEvent('app_open', { cold: true, ...launchSource() });
  if (!isNative) return () => {};
  let hiddenAt: number | null = null;
  const pause = App.addListener('pause', () => { hiddenAt = Date.now(); });
  const resume = App.addListener('resume', () => {
    if (hiddenAt !== null && Date.now() - hiddenAt >= REOPEN_AFTER_MS) {
      logAppEvent('app_open', { cold: false });
    }
    hiddenAt = null;
  });
  return () => {
    void pause.then((h) => h.remove());
    void resume.then((h) => h.remove());
  };
}
